import { Box, Skeleton, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { useTransactionsFeeDetail } from "Api/Queries/Transactions/useTransactionsFeeDetail";
import { useTransactionStatus } from "Api/Queries/Transactions/useTransactionStatus";
import { useBankReturnRelay } from "Hooks/Transactions/useBankReturnRelay";
import { useGatewayReturnRedirect } from "Hooks/Transactions/useGatewayReturnRedirect";
import { useTransactionStatusSignalR } from "Hooks/Transactions/useTransactionStatusSignalR";
import { usePayPointQuery } from "Api/Queries/PayPoints/usePayPointQuery";
import { PaymentStatus } from "Components/Transactions/PaymentStatus";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { CurrencyCode } from "Models/CurrencyCodes";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";
import { type ElementType, type ReactNode, useEffect } from "react";
import styled from "styled-components";
import { TransactionDetailActions } from "Components/Transactions/TransactionDetailActions/TransactionDetailActions";
import { TransactionDirection } from "Infrastructure/Api/Api";
import {
  getTransactionDirection,
  getTransactionRefundState,
  refundStateToLabelKey,
  type ViewerTransactionDirection,
} from "Utils/TransactionUtils";
import { useLoggedInUser } from "Hooks/useLoggedInUser";

type Props = {
  publicID: string;
};

// CZK amounts render narrow (symbol suffix); other currencies need a wider value column.
const Row = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 1fr;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const WideRow = styled(Row)`
  grid-template-columns: 2fr 4fr;
`;

const Left = styled(Typography)`
  font-size: 16px;
`;

const Right = styled(Typography)`
  font-size: 18px;
  font-weight: 500;
  justify-self: flex-end;
  word-break: break-word;
  text-align: end;
`;

const ComponentResources = Resources.Merchant.InitiateDetail;

const LoadingRowWrapper = ({
  children,
  isLoading,
  rowComponent: RowComponent = Row,
}: {
  isLoading?: boolean;
  children: ReactNode;
  rowComponent?: ElementType;
}) => {
  if (isLoading) {
    return (
      <RowComponent>
        <Left>
          <Skeleton width="125px" />
        </Left>
        <Right>
          <Skeleton width="60px" />
        </Right>
      </RowComponent>
    );
  }

  return <>{children}</>;
};

export const TransactionDetail = ({ publicID }: Props) => {
  const userRole = useCurrentUserRole();
  const { t } = useResource();
  const { data: transactionsDetail, isLoading: isLoadingTransactionsDetail } =
    useTransactionsDetail({ publicID });
  const { data: status, isLoading: isLoadingStatus } =
    useTransactionStatus(publicID);
  useTransactionStatusSignalR({ publicID });
  // Banks returning straight to the app carry the sign outcome only in this URL — hand it to the backend.
  useBankReturnRelay({ publicID });
  const { data: feeDetail, isLoading: isLoadingFeeDetail } =
    useTransactionsFeeDetail(publicID);
  const { data: paypoint } = usePayPointQuery(
    transactionsDetail?.payPointPublicID,
  );
  const user = useLoggedInUser();

  useEffect(() => {
    if (transactionsDetail?.signingUrl) {
      window.location.href = transactionsDetail.signingUrl;
    }
  }, [transactionsDetail?.signingUrl]);

  const currencyCode = transactionsDetail?.currencyCode;
  const isCZKCurrency = currencyCode === CurrencyCode.CZK;
  // One decision point for the row layout instead of flagging every row.
  const DetailRow = isCZKCurrency ? Row : WideRow;

  const direction: ViewerTransactionDirection =
    transactionsDetail && user
      ? getTransactionDirection(
          {
            creditorID: transactionsDetail.creditorID,
            debtorID: transactionsDetail.debtorID,
          },
          user.userID,
        )
      : TransactionDirection.Unknown;

  // E-commerce gateway payments: send the paying customer back to the e-shop once finished.
  // Only the debtor is redirected — the merchant opening the same detail must stay in the app.
  useGatewayReturnRedirect({
    transactionPublicID: publicID,
    transactionStatus: status?.transactionStatus,
    returnUrl: status?.returnUrl,
    isEnabled: direction === TransactionDirection.Outgoing,
  });

  const transactionStatus =
    status?.transactionStatus ?? transactionsDetail?.transactionStatus;
  const failureReason =
    status?.failureReason ?? transactionsDetail?.failureReason;

  const isTransactionStatusLoading =
    !transactionStatus && (isLoadingStatus || isLoadingTransactionsDetail);

  // ZS-1676: a refunded original stays Success — the refund state derives from the inverse row.
  const refundState = getTransactionRefundState({
    status: transactionStatus,
    isRefund: transactionsDetail?.isRefund,
    relatedTransactionStatus: transactionsDetail?.relatedTransactionStatus,
  });

  return (
    <PrimaryPaper>
      <LoadingRowWrapper
        isLoading={isLoadingTransactionsDetail}
        rowComponent={DetailRow}
      >
        {!!transactionsDetail?.creditorIBAN && (
          <DetailRow>
            <Left>IBAN</Left>
            <Right>{transactionsDetail?.creditorIBAN}</Right>
          </DetailRow>
        )}
      </LoadingRowWrapper>

      <LoadingRowWrapper
        isLoading={isLoadingTransactionsDetail}
        rowComponent={DetailRow}
      >
        {!!transactionsDetail?.paymentReference && (
          <DetailRow>
            <Left>{t(Resources.Common.VariableSymbol)}</Left>
            <Right>{transactionsDetail.paymentReference}</Right>
          </DetailRow>
        )}
      </LoadingRowWrapper>

      <LoadingRowWrapper
        isLoading={isLoadingTransactionsDetail}
        rowComponent={DetailRow}
      >
        {!!transactionsDetail?.amount && (
          <>
            <DetailRow>
              <Left>{t(Resources.Common.Amount)}</Left>
              <Right>
                {formatCurrency({
                  amount:
                    transactionsDetail.amount - transactionsDetail.tipAmount,
                  currencyCode,
                })}
              </Right>
            </DetailRow>
            {transactionsDetail.tipAmount > 0 && (
              <>
                <DetailRow>
                  <Left>{t(Resources.Customer.Pay.Tip)}</Left>
                  <Right>
                    {formatCurrency({
                      amount: transactionsDetail.tipAmount,
                      currencyCode,
                    })}
                  </Right>
                </DetailRow>
                <DetailRow>
                  <Left>{t(Resources.Common.TotalAmount)}</Left>
                  <Right>
                    {formatCurrency({
                      amount: transactionsDetail.amount,
                      currencyCode,
                    })}
                  </Right>
                </DetailRow>
              </>
            )}
          </>
        )}
      </LoadingRowWrapper>

      {!!transactionsDetail?.noteForDebtor && (
        <DetailRow>
          <Left>{t(Resources.Common.Note)}</Left>
          <Right>{transactionsDetail?.noteForDebtor}</Right>
        </DetailRow>
      )}

      <LoadingRowWrapper
        isLoading={isLoadingTransactionsDetail}
        rowComponent={DetailRow}
      >
        <DetailRow>
          <Left>{t(Resources.Common.TransactionPaymentCode)}</Left>
          <Right>{transactionsDetail?.publicID}</Right>
        </DetailRow>
      </LoadingRowWrapper>

      {userRole === AccessRoleCodes.Merchant && !!feeDetail?.totalFee && (
        <LoadingRowWrapper
          isLoading={isLoadingFeeDetail}
          rowComponent={DetailRow}
        >
          <DetailRow>
            <Left>{t(ComponentResources.TransactionFee)}</Left>
            <Right>
              {/* The fee is denominated in the creditor market's currency, which
                  differs from the transaction currency on cross-currency payments. */}
              {formatCurrency({
                amount: feeDetail?.totalFee,
                currencyCode: feeDetail?.totalFeeCurrencyCode,
              })}
            </Right>
          </DetailRow>
        </LoadingRowWrapper>
      )}

      {!!paypoint?.name && (
        <DetailRow>
          <Left>{t(Resources.Paypoint.Paypoint)}</Left>
          <Right>{paypoint.name}</Right>
        </DetailRow>
      )}

      {refundState && (
        <DetailRow>
          <Left>{t(Resources.Transactions.RefundState.Title)}</Left>
          <Right>{t(refundStateToLabelKey[refundState])}</Right>
        </DetailRow>
      )}

      <Stack mt={10} gap={8}>
        <Box textAlign="center">
          <PaymentStatus
            transactionStatus={transactionStatus}
            isLoading={isTransactionStatusLoading}
            failureReason={failureReason}
          />
        </Box>
        <TransactionDetailActions
          publicID={publicID}
          transactionsDetail={transactionsDetail}
          direction={direction}
          transactionStatus={transactionStatus}
          isLoadingStatus={isLoadingStatus}
        />
      </Stack>
    </PrimaryPaper>
  );
};
